import type { CameraKey } from './cameraPresets'

/**
 * Every camera instance that is actually bound to a body, tracked independently of the
 * React store.
 *
 * The quit path used to read `useCameraStore` to decide what to say goodbye to. That is
 * the wrong source of truth: the store holds what the UI should DISPLAY, and several
 * paths legitimately clear it without releasing the camera. The health monitor is the
 * important one — after two unanswered probes it does
 *
 *     useCameraStore.getState().setCamera(null)
 *     useCameraStore.getState().setWebcam(null)
 *
 * so the reconnect prompt can appear. It deliberately does NOT call disconnect(), because
 * the camera is by definition not answering and a blocking `gp_camera_exit` there would
 * freeze the page. Reasonable — but it drops the only reference to a Camera that still
 * holds the USB claim and an open PTP session.
 *
 * The consequences are exactly what the field reported (2026-09-17): closing the app from
 * the welcome screen made no shutter click, because the store was empty and the goodbye
 * found "nothing to release" — while closing from the settings modal, where a preview
 * keeps the store populated, clicked normally. And because the body never received
 * SetRemoteMode(1) + CloseSession, it stayed in remote mode with a live session, which is
 * why the NEXT launch reconnected "often, but sometimes not".
 *
 * So the registry remembers the instance even after the store forgets it, and the quit
 * path releases the union of both. Releasing an orphan is best-effort and bounded by the
 * caller's budget: it may already be gone, and a body that cannot answer must not hold
 * the shutdown.
 */

/** Anything the goodbye can be said to. Both Camera and NativeCamera expose this. */
export interface ReleasableCamera {
  disconnect: () => Promise<void>
}

const live = new Map<CameraKey, ReleasableCamera>()

/** This slot is now bound to `camera`. Replaces whatever it held. */
export function rememberLiveCamera(cameraKey: CameraKey, camera: ReleasableCamera | null): void {
  if (camera) live.set(cameraKey, camera)
  else live.delete(cameraKey)
}

/**
 * This camera has been released. Passing the instance only forgets it if it is still the
 * registered one, so a late teardown cannot evict a newer connection's camera.
 */
export function forgetLiveCamera(cameraKey: CameraKey, camera?: ReleasableCamera): void {
  if (camera && live.get(cameraKey) !== camera) return
  live.delete(cameraKey)
}

/** Everything still believed to hold a body, whatever the store currently shows. */
export function getLiveCameras(): { cameraKey: CameraKey; camera: ReleasableCamera }[] {
  return [...live.entries()].map(([cameraKey, camera]) => ({ cameraKey, camera }))
}

/** Test seam. */
export function resetLiveCameraRegistry(): void {
  live.clear()
}
